/**
 * Corrective Action Report Tab (Step 3) for NCR Edit
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Save, Send, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { CarScoreFields } from './CarScoreFields';
import { ScoreCriteriaPanel } from './ScoreCriteriaPanel';
import { CORRECTIVE_ACTION_DIMENSIONS } from './scoreCriteria';
import { NCREditFormData } from './types';
import { LeadTimeTracking } from './LeadTimeTracking';
import type { CommonisedCorrectiveAction } from '../../lib/api';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { missingCarScores } from './carScoreCheck';
import { toast } from 'sonner';

interface Step3TabProps {
  formData: NCREditFormData;
  /** Supplier may edit + save the CAR fields (true at 'created' so the CAR
   *  can be filled alongside the RCA, and at 'ca_submitted'). */
  canEdit: boolean;
  /** Supplier may submit the CAR (only at 'ca_submitted' — i.e. after the
   *  RCA has been submitted). Defaults to canEdit for back-compat. */
  canSubmit?: boolean;
  /** Who does the 8D, for the card title (E23): 'Internal', 'Customer' or
   *  'Supplier Only' (ownership.ts eightDOwnerLabel). */
  ownerLabel?: string;
  saving: boolean;
  // CR-6000: per-field validation errors for the CAR section, keyed by API
  // field name (corrective_action, preventive_action, impact_assessment).
  // When a key is present the corresponding Textarea renders with a red border
  // and an error message beneath it. Mirrors the RCA tab's fieldErrors prop.
  fieldErrors?: Record<string, string>;
  onUpdateFormData: (field: string, value: unknown) => void;
  onSave: () => void;
  onSubmitCAR: () => void;
  // CR-6024 r2 (Jacq rework): customer-side Approve / Reject & Rework buttons
  // moved up here so they sit directly under the CAR the customer is reviewing
  // (previously they were at the very bottom of the page, after the action log).
  // Only rendered when status == 'car_submitted' AND the viewer is customer.
  showCustomerActions?: boolean;
  onApproveClick?: () => void;
  onRejectClick?: () => void;
  // 8D scoring: RC/CA scores (1-5) the customer grades the CAR with before
  // clicking Approve / Reject & Rework. Held by useNCREdit and sent with
  // whichever decision is taken.
  // CR-7357: four grades — ORC (rc), ERC, OPA (ca), EPA.
  rcScore?: number | null;
  ercScore?: number | null;
  caScore?: number | null;
  epaScore?: number | null;
  onRcScoreChange?: (score: number) => void;
  onErcScoreChange?: (score: number) => void;
  onCaScoreChange?: (score: number) => void;
  onEpaScoreChange?: (score: number) => void;
  /** Customer review 2026-07: master list backing the commonised CA dropdown. */
  commonisedActions?: CommonisedCorrectiveAction[];
  /** Approve is gated on the claim workflows (collection/credit/replacement)
   *  being complete — Reject & Rework is always available at car_submitted.
   *  Customer review 2026-07-08 (DMR 71216): both used to be hidden. */
  canApprove?: boolean;
  /** E28 (29 Sep 2026): false when the customer owns the 8D -- there is no
   *  supplier's work to grade, so no grades are asked for or shown. */
  graded?: boolean;
  /** CR-7353: this NCR's customer has the Escape chain switched on (Admin
   *  Setup → Quality). On → the CAR splits into an Occurrence pair and an
   *  Escape pair, side by side; off → the single corrective/preventive pair
   *  as before the split, with no Escape fields and no "Occurrence" wording. */
  escapeRootCauseEnabled?: boolean;
}

export function CorrectiveActionTab({
  formData,
  canEdit,
  canSubmit,
  ownerLabel = 'Supplier Only',
  saving,
  fieldErrors = {},
  onUpdateFormData,
  onSave,
  onSubmitCAR,
  showCustomerActions,
  onApproveClick,
  onRejectClick,
  rcScore,
  ercScore,
  caScore,
  epaScore,
  onRcScoreChange,
  onErcScoreChange,
  onCaScoreChange,
  onEpaScoreChange,
  commonisedActions = [],
  canApprove = true,
  graded = true,
  escapeRootCauseEnabled = false,
}: Step3TabProps) {
  // 8D/A3 scoring master switch (Admin Setup > Features). When off, the RC/CA
  // grading dropdowns are hidden; the Approve / Reject & Rework workflow stays.
  const { enable8dScoring: scoringOn } = useMenuSettings();
  const enable8dScoring = scoringOn && graded;
  // CR-6000: shared styling/helper for inline required-field errors, matching
  // the RCA tab so the two supplier submit flows look identical.
  const errorTextareaClass =
    'border-red-500 focus-visible:ring-red-500 focus-visible:border-red-500';
  const renderError = (field: string) =>
    fieldErrors[field] ? (
      <p
        id={`${field}-error`}
        role="alert"
        className="text-sm text-red-600 mt-1"
      >
        {fieldErrors[field]}
      </p>
    ) : null;

  return (
      <Card>
        <CardHeader>
          <CardTitle>Corrective Action Report ({ownerLabel})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* CR-6024: surface the customer's rejection reason when the CAR
                was sent back for rework. Sits at the top of the tab so the
                supplier sees the required change before they start editing. */}
            {typeof formData['car_rejection_reason'] === 'string' && (formData['car_rejection_reason'] as string).trim() && (
              <div className="border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200">CAR returned for rework</h4>
                    <p className="text-sm text-amber-900/90 dark:text-amber-100 mt-1 whitespace-pre-wrap">
                      {formData['car_rejection_reason'] as string}
                    </p>
                    <p className="text-xs text-amber-800/70 dark:text-amber-200/70 mt-2">
                      Please review the rejection reason, amend the Corrective Action Report below, and resubmit.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* CR-7356: scoring criteria for the Corrective Action dimensions
                (Occurrence + Escape Prevention Actions), so the supplier can see
                how the 8D is graded while completing it. Switched with the rest
                of DE's four-dimension 8D. */}
            {escapeRootCauseEnabled && graded && (
              <ScoreCriteriaPanel dimensions={CORRECTIVE_ACTION_DIMENSIONS} />
            )}

            {/* E6: when the corrective action is due, beside it (legacy parity). */}
            <LeadTimeTracking step="corrective_action" formData={formData} />

            {/* CR-7354/CR-7353: with the Escape chain on, the Corrective Action
                Report splits into two self-contained reports side by side — an
                Occurrence report and an Escape report — each with its own
                completion date, corrective + preventive actions and impact
                assessment. corrective_action / preventive_action /
                impact_assessment / action_completion_date are the Occurrence
                fields (names unchanged). Off, the single report renders
                full-width with no "Occurrence" wording and no Escape fields,
                exactly as before the split. Dates save via the standard NCR
                PATCH flow alongside the other CAR fields. */}
            {escapeRootCauseEnabled ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <Label>8D/A3 Action Completion Date</Label>
                    <Input
                      type="date"
                      value={(formData.action_completion_date as string) || ''}
                      onChange={(e) => onUpdateFormData('action_completion_date', e.target.value || null)}
                      disabled={!canEdit}
                    />
                  </div>
                  <div>
                    <Label>Occurrence Corrective Action *</Label>
                    <Textarea
                      value={formData.corrective_action || ''}
                      onChange={(e) => onUpdateFormData('corrective_action', e.target.value)}
                      disabled={!canEdit}
                      rows={4}
                      aria-invalid={!!fieldErrors['corrective_action']}
                      aria-describedby={fieldErrors['corrective_action'] ? 'corrective_action-error' : undefined}
                      className={fieldErrors['corrective_action'] ? errorTextareaClass : ''}
                    />
                    {renderError('corrective_action')}
                  </div>
                  <div>
                    <Label>Occurrence Preventive Action *</Label>
                    <Textarea
                      value={formData.preventive_action || ''}
                      onChange={(e) => onUpdateFormData('preventive_action', e.target.value)}
                      disabled={!canEdit}
                      rows={4}
                      aria-invalid={!!fieldErrors['preventive_action']}
                      aria-describedby={fieldErrors['preventive_action'] ? 'preventive_action-error' : undefined}
                      className={fieldErrors['preventive_action'] ? errorTextareaClass : ''}
                    />
                    {renderError('preventive_action')}
                  </div>
                  <div>
                    <Label>Occurrence Impact Assessment *</Label>
                    <Textarea
                      value={formData.impact_assessment || ''}
                      onChange={(e) => onUpdateFormData('impact_assessment', e.target.value)}
                      disabled={!canEdit}
                      rows={3}
                      maxLength={500}
                      aria-invalid={!!fieldErrors['impact_assessment']}
                      aria-describedby={fieldErrors['impact_assessment'] ? 'impact_assessment-error' : undefined}
                      className={fieldErrors['impact_assessment'] ? errorTextareaClass : ''}
                    />
                    {renderError('impact_assessment')}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Escape Action Completion Date</Label>
                    <Input
                      type="date"
                      value={(formData.escape_action_completion_date as string) || ''}
                      onChange={(e) => onUpdateFormData('escape_action_completion_date', e.target.value || null)}
                      disabled={!canEdit}
                    />
                  </div>
                  <div>
                    <Label>Escape Corrective Action</Label>
                    <Textarea
                      value={(formData.escape_corrective_action as string) || ''}
                      onChange={(e) => onUpdateFormData('escape_corrective_action', e.target.value)}
                      disabled={!canEdit}
                      rows={4}
                      placeholder="How the escape (why the defect reached the customer) is being corrected"
                    />
                  </div>
                  <div>
                    <Label>Escape Preventive Action</Label>
                    <Textarea
                      value={(formData.escape_preventive_action as string) || ''}
                      onChange={(e) => onUpdateFormData('escape_preventive_action', e.target.value)}
                      disabled={!canEdit}
                      rows={4}
                      placeholder="How a similar escape is prevented in future"
                    />
                  </div>
                  <div>
                    <Label>Escape Impact Assessment</Label>
                    <Textarea
                      value={(formData.escape_impact_assessment as string) || ''}
                      onChange={(e) => onUpdateFormData('escape_impact_assessment', e.target.value)}
                      disabled={!canEdit}
                      rows={3}
                      maxLength={500}
                      placeholder="Impact of the escape and the areas it affected"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <Label>8D/A3 Action Completion Date</Label>
                  <Input
                    type="date"
                    value={(formData.action_completion_date as string) || ''}
                    onChange={(e) => onUpdateFormData('action_completion_date', e.target.value || null)}
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <Label>Corrective Action *</Label>
                  <Textarea
                    value={formData.corrective_action || ''}
                    onChange={(e) => onUpdateFormData('corrective_action', e.target.value)}
                    disabled={!canEdit}
                    rows={4}
                    aria-invalid={!!fieldErrors['corrective_action']}
                    aria-describedby={fieldErrors['corrective_action'] ? 'corrective_action-error' : undefined}
                    className={fieldErrors['corrective_action'] ? errorTextareaClass : ''}
                  />
                  {renderError('corrective_action')}
                </div>
                <div>
                  <Label>Preventive Action *</Label>
                  <Textarea
                    value={formData.preventive_action || ''}
                    onChange={(e) => onUpdateFormData('preventive_action', e.target.value)}
                    disabled={!canEdit}
                    rows={4}
                    aria-invalid={!!fieldErrors['preventive_action']}
                    aria-describedby={fieldErrors['preventive_action'] ? 'preventive_action-error' : undefined}
                    className={fieldErrors['preventive_action'] ? errorTextareaClass : ''}
                  />
                  {renderError('preventive_action')}
                </div>
                <div>
                  <Label>Impact Assessment *</Label>
                  <Textarea
                    value={formData.impact_assessment || ''}
                    onChange={(e) => onUpdateFormData('impact_assessment', e.target.value)}
                    disabled={!canEdit}
                    rows={3}
                    maxLength={500}
                    aria-invalid={!!fieldErrors['impact_assessment']}
                    aria-describedby={fieldErrors['impact_assessment'] ? 'impact_assessment-error' : undefined}
                    className={fieldErrors['impact_assessment'] ? errorTextareaClass : ''}
                  />
                  {renderError('impact_assessment')}
                </div>
              </>
            )}

            {/* 8D Step 6-7: Impact on Similar Products */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold mb-3">8D D6-D7: Impact on Similar Products</h4>
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  checked={formData.similar_products_affected || false}
                  onChange={(e) => onUpdateFormData('similar_products_affected', e.target.checked)}
                  disabled={!canEdit}
                  className="disabled:cursor-not-allowed"
                />
                <Label>Similar products/processes affected by this issue?</Label>
              </div>

              {formData.similar_products_affected && (
                <div>
                  <Label>Impact Analysis on Similar Products</Label>
                  <Textarea
                    value={formData.impact_on_similar_products || ''}
                    onChange={(e) => onUpdateFormData('impact_on_similar_products', e.target.value)}
                    disabled={!canEdit}
                    rows={3}
                    placeholder="Describe which similar products/processes are affected and what actions have been taken..."
                  />
                </div>
              )}
            </div>

            {/* 8D Step 6: Training Requirements */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold mb-3">8D D6: Training Requirements</h4>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.training_required || false}
                  onChange={(e) => onUpdateFormData('training_required', e.target.checked)}
                  disabled={!canEdit}
                  className="disabled:cursor-not-allowed"
                />
                <Label>Training Required</Label>
              </div>
            </div>

            {formData.training_required && (
              <div>
                <Label>Training Details</Label>
                <Textarea
                  value={formData.training_details || ''}
                  onChange={(e) => onUpdateFormData('training_details', e.target.value)}
                  disabled={!canEdit}
                  rows={2}
                />
              </div>
            )}

            {/* 8D Step 6: Documentation Updates */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold mb-3">8D D6: Documentation Updates</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.doc_process_flow_updated || false}
                    onChange={(e) => onUpdateFormData('doc_process_flow_updated', e.target.checked)}
                    disabled={!canEdit}
                    className="disabled:cursor-not-allowed"
                  />
                  <Label>Process Flow Diagram Updated</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.doc_control_plan_updated || false}
                    onChange={(e) => onUpdateFormData('doc_control_plan_updated', e.target.checked)}
                    disabled={!canEdit}
                    className="disabled:cursor-not-allowed"
                  />
                  <Label>Control Plan Updated</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.doc_fmea_updated || false}
                    onChange={(e) => onUpdateFormData('doc_fmea_updated', e.target.checked)}
                    disabled={!canEdit}
                    className="disabled:cursor-not-allowed"
                  />
                  <Label>FMEA (Failure Mode Effects Analysis) Updated</Label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.doc_work_instructions_updated || false}
                    onChange={(e) => onUpdateFormData('doc_work_instructions_updated', e.target.checked)}
                    disabled={!canEdit}
                    className="disabled:cursor-not-allowed"
                  />
                  <Label>Work Instructions Updated</Label>
                </div>
                <div className="mt-3">
                  <Label>Other Documentation Updated (specify)</Label>
                  <Textarea
                    value={formData.doc_other_updated || ''}
                    onChange={(e) => onUpdateFormData('doc_other_updated', e.target.value)}
                    disabled={!canEdit}
                    rows={2}
                    placeholder="Specify any other documentation that was updated..."
                  />
                </div>
                {/* Customer review 2026-07: standardised CA from the master
                    list (managed via Data Import → Reference Data). */}
                <div className="mt-3">
                  <Label>Commonised Corrective Action</Label>
                  <select
                    value={String((formData.commonised_corrective_action as number | null) ?? '')}
                    onChange={(e) => onUpdateFormData('commonised_corrective_action', e.target.value ? Number(e.target.value) : null)}
                    disabled={!canEdit}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select...</option>
                    {commonisedActions.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div>
              <Label>Documentation References</Label>
              <Textarea
                value={formData.documentation_references || ''}
                onChange={(e) => onUpdateFormData('documentation_references', e.target.value)}
                disabled={!canEdit}
                rows={2}
              />
            </div>

            {/* Customer review 2026-07: 8D D7 horizontal deployment — which
                other part numbers the actions should be rolled out to. */}
            <div className="border-t pt-4 mt-4">
              <h4 className="text-sm font-semibold mb-3">8D D7: Horizontal Deployment</h4>
              <Label>Applicable part numbers for actions horizontal deployment</Label>
              <Textarea
                value={(formData.horizontal_deployment_parts as string) || ''}
                onChange={(e) => onUpdateFormData('horizontal_deployment_parts', e.target.value)}
                disabled={!canEdit}
                rows={2}
                placeholder="e.g. 1004414, 1004415 — parts the same actions apply to"
              />
              {/* CR-7355: 8D D7 System Learning — captured alongside the
                  horizontal-deployment part list. Its own group (like the
                  other fields) so the label spaces off the textarea above.
                  DE's box, so it rides the same switch; the backend ignores
                  the field for a client with the switch off. */}
              {escapeRootCauseEnabled && (
                <div className="mt-4">
                  <Label>System Learning</Label>
                  <Textarea
                    value={(formData.system_learning as string) || ''}
                    onChange={(e) => onUpdateFormData('system_learning', e.target.value)}
                    disabled={!canEdit}
                    rows={2}
                    placeholder="What did the wider system learn from this issue?"
                  />
                </div>
              )}
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.corrective_action_notes || ''}
                onChange={(e) => onUpdateFormData('corrective_action_notes', e.target.value)}
                disabled={!canEdit}
                rows={2}
                maxLength={500}
              />
            </div>

            {canEdit && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button onClick={onSave} disabled={saving} variant="outline">
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                  {(canSubmit ?? canEdit) && (
                    <Button onClick={onSubmitCAR} disabled={saving}>
                      <Send className="h-4 w-4 mr-2" />
                      Submit CAR
                    </Button>
                  )}
                </div>
                {!(canSubmit ?? canEdit) && (
                  <p className="text-xs text-muted-foreground">
                    You can fill in and save the Corrective Action Report now. Submit the
                    Root Cause Analysis first, then submit the CAR.
                  </p>
                )}
              </div>
            )}

            {/* Scores outlive the review window. showCustomerActions is only
                true while the CAR sits at 'car_submitted' AND the viewer is the
                reviewing customer, so approving (-> 'closed') used to unmount
                the grades the customer had just entered — they were saved but
                no longer visible anywhere on the NCR. Render them read-only
                whenever the editable block is absent but a grade exists. */}
            {/* Only count the grades that are actually rendered — with the
                Escape chain off an NCR carrying only a legacy ERC/EPA grade
                would otherwise open an empty panel. */}
            {!showCustomerActions && enable8dScoring && (
              rcScore != null || caScore != null
              || (escapeRootCauseEnabled && (ercScore != null || epaScore != null))
            ) && (
              <div className="border-t pt-4 mt-2">
                <CarScoreFields editable={false} fourDimension={escapeRootCauseEnabled} rcScore={rcScore} ercScore={ercScore} caScore={caScore} epaScore={epaScore} />
              </div>
            )}

            {/* CR-6024 r2: customer-side review actions, placed directly below
                the CAR content per Jacq's rework note (previously at the very
                bottom of the page, after the Action Log). */}
            {showCustomerActions && (
              <div className="border-t pt-4 mt-2">
                <p className="text-sm text-muted-foreground mb-3">
                  Review the Corrective Action Report above,{enable8dScoring ? ' score every area,' : ''} then
                  either approve the 8D or reject &amp; send it back{ownerLabel === 'Supplier Only' ? ' to the supplier' : ''} for rework. The NCR
                  closes once every required action (collection, credit, replacement) is also complete.
                </p>
                {/* 8D scoring: RC/CA grades (1-5) captured with the decision.
                    Hidden when the 8D/A3 scoring feature is disabled for the tenant. */}
                {enable8dScoring && (
                  <CarScoreFields
                    fourDimension={escapeRootCauseEnabled}
                    editable
                    rcScore={rcScore}
                    ercScore={ercScore}
                    caScore={caScore}
                    epaScore={epaScore}
                    onRcScoreChange={onRcScoreChange}
                    onErcScoreChange={onErcScoreChange}
                    onCaScoreChange={onCaScoreChange}
                    onEpaScoreChange={onEpaScoreChange}
                  />
                )}
                <div className="flex flex-wrap gap-3 justify-end items-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onRejectClick?.()}
                    size="lg"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject &amp; Rework
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      // E17: name the missing grades now, not after the confirm.
                      const missing = enable8dScoring
                        ? missingCarScores({ rcScore, ercScore, caScore, epaScore }, escapeRootCauseEnabled)
                        : [];
                      if (missing.length) {
                        toast.error(`Score every area before approving the 8D: ${missing.join(', ')}.`);
                        return;
                      }
                      onApproveClick?.();
                    }}
                    disabled={!canApprove}
                    size="lg"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
  );
}
